import { useState } from "react";
import { Share2, Copy, Check, Clock, Link as LinkIcon } from "lucide-react";
import { Button } from "@/components/ui";
import {
  CUSTOM_EXPIRY_VALUE,
  DEFAULT_EXPIRY_MINUTES,
  EXPIRY_PRESETS,
  MAX_CUSTOM_EXPIRY_DAYS,
  daysToMinutes,
} from "@/features/docs/constants/expiry";


interface ShareDocDialogProps {
  pageId: string;
  pageTitle: string;
  scope?: "all" | "folder" | "page";
  folderId?: string;
  /** Render prop — the trigger button */
  children: (open: () => void) => React.ReactNode;
}

type ExpirySelection = number | typeof CUSTOM_EXPIRY_VALUE;

interface MagicLinkResponse {
  magic_link_url: string;
  customer_name: string;
  scope: string;
  folder_id: string | null;
  expires_in_minutes: number;
  page_id: string | null;
}

export function ShareDocDialog({ pageId, pageTitle, scope = "all", folderId, children }: ShareDocDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [expiry, setExpiry] = useState<ExpirySelection>(DEFAULT_EXPIRY_MINUTES);
  const [customDays, setCustomDays] = useState("");
  const [generating, setGenerating] = useState(false);
  const [link, setLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  function open() {
    setIsOpen(true);
    setLink("");
    setError("");
    setCopied(false);
  }

  function close() {
    setIsOpen(false);
    setCustomerName("");
    setExpiry(DEFAULT_EXPIRY_MINUTES);
    setCustomDays("");
    setLink("");
    setError("");
    setCopied(false);
  }

  async function generateLink() {
    if (!customerName.trim()) {
      setError("Enter the customer name");
      return;
    }

    let expiresInMinutes: number;
    if (expiry === CUSTOM_EXPIRY_VALUE) {
      const days = Number(customDays);
      if (!customDays.trim() || !Number.isFinite(days) || days <= 0) {
        setError("Enter a number of days greater than 0");
        return;
      }
      if (days > MAX_CUSTOM_EXPIRY_DAYS) {
        setError(`Maximum is ${MAX_CUSTOM_EXPIRY_DAYS} days`);
        return;
      }
      expiresInMinutes = daysToMinutes(days);
    } else {
      expiresInMinutes = expiry;
    }

    setGenerating(true);
    setError("");

    try {
      const requestBody: Record<string, unknown> = {
        customer_name: customerName.trim(),
        scope,
        expires_in_minutes: expiresInMinutes,
      };
      if (scope === "folder" && folderId) requestBody.folder_id = folderId;
      if (scope === "page" && pageId) requestBody.page_id = pageId;

      // Use raw fetch — docs endpoints return Pydantic models directly,
      // not the standard ApiResponse envelope that apiClient expects.
      const token = localStorage.getItem("docs_jwt");
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch("/api/docs/magic-link", {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify(requestBody),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        throw new Error(errBody?.detail ?? `Request failed (${res.status})`);
      }

      const data: MagicLinkResponse = await res.json();
      const fullUrl = `${window.location.origin}${data.magic_link_url}`;
      setLink(fullUrl);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to generate link";
      setError(msg);
    } finally {
      setGenerating(false);
    }
  }

  async function copyLink() {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (!isOpen) return <>{children(open)}</>;

  return (
    <>
      {children(open)}

      {/* Backdrop */}
      <div className="fixed inset-0 z-50 bg-black/40" onClick={close} />

      {/* Dialog */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="w-full max-w-md rounded-ev border border-ev-border bg-ev-surface p-6 shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mb-4 flex items-center gap-2">
            <Share2 className="h-5 w-5 text-ev-info-text" />
            <h2 className="text-lg font-semibold text-ev-text-primary">
              Share Explainer
            </h2>
          </div>

          <p className="mb-4 text-sm text-ev-text-secondary">
            {scope === "page" ? (
              <>
                Generate a magic link for <span className="font-medium text-ev-text-primary">{pageTitle}</span>.
                The recipient sees only this explainer.
              </>
            ) : scope === "folder" ? (
              <>
                Generate a magic link for the <span className="font-medium text-ev-text-primary">{pageTitle}</span> folder.
                The recipient sees only explainers in this folder.
              </>
            ) : (
              <>
                Generate a magic link to the <span className="font-medium text-ev-text-primary">Explainers Library</span>.
                The recipient sees all explainers across all folders.
              </>
            )}
          </p>

          {!link ? (
            <>
              {/* Customer name input */}
              <div className="mb-3">
                <label className="mb-1 block text-xs font-medium text-ev-text-secondary">
                  Customer name
                </label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Plan Health Co"
                  className="w-full rounded-ev border border-ev-border bg-ev-surface px-3 py-2 text-sm text-ev-text-primary placeholder:text-ev-text-muted focus:border-ev-info-text focus:outline-none focus:ring-1 focus:ring-ev-info-text"
                  onKeyDown={(e) => e.key === "Enter" && generateLink()}
                />
              </div>

              {/* Expiry selector */}
              <div className="mb-4">
                <label className="mb-1 block text-xs font-medium text-ev-text-secondary">
                  Link expires in
                </label>
                <div className="flex flex-wrap gap-2">
                  {EXPIRY_PRESETS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setExpiry(opt.value)}
                      className={`flex items-center gap-1 rounded-ev px-2.5 py-1.5 text-xs font-medium transition-colors ${
                        expiry === opt.value
                          ? "bg-ev-blue text-white"
                          : "border border-ev-border text-ev-text-secondary hover:bg-ev-surface-subtle"
                      }`}
                    >
                      <Clock className="h-3 w-3" />
                      {opt.label}
                    </button>
                  ))}
                  <button
                    onClick={() => setExpiry(CUSTOM_EXPIRY_VALUE)}
                    className={`flex items-center gap-1 rounded-ev px-2.5 py-1.5 text-xs font-medium transition-colors ${
                      expiry === CUSTOM_EXPIRY_VALUE
                        ? "bg-ev-blue text-white"
                        : "border border-ev-border text-ev-text-secondary hover:bg-ev-surface-subtle"
                    }`}
                  >
                    <Clock className="h-3 w-3" />
                    Custom…
                  </button>
                </div>
                {expiry === CUSTOM_EXPIRY_VALUE && (
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      max={MAX_CUSTOM_EXPIRY_DAYS}
                      step={1}
                      value={customDays}
                      onChange={(e) => setCustomDays(e.target.value)}
                      placeholder="Days"
                      aria-label="Custom expiry in days"
                      className="w-28 rounded-ev border border-ev-border bg-ev-surface px-2 py-1 text-xs text-ev-text-primary placeholder:text-ev-text-muted focus:border-ev-info-text focus:outline-none focus:ring-1 focus:ring-ev-info-text"
                    />
                    <span className="text-xs text-ev-text-muted">
                      days (max {MAX_CUSTOM_EXPIRY_DAYS})
                    </span>
                  </div>
                )}
              </div>

              {error && (
                <div className="mb-3 rounded-ev border border-ev-danger-border bg-ev-danger-bg p-2 text-xs text-ev-danger-text">
                  {error}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={close}>
                  Cancel
                </Button>
                <Button onClick={generateLink} disabled={generating}>
                  <LinkIcon className="mr-1.5 h-4 w-4" />
                  {generating ? "Generating..." : "Generate Link"}
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Generated link */}
              <div className="mb-4 rounded-ev border border-ev-success-border bg-ev-success-bg p-3">
                <p className="mb-2 text-xs font-medium text-ev-success-text">
                  Magic link generated
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={link}
                    readOnly
                    className="flex-1 rounded border border-ev-border bg-ev-surface px-2 py-1 text-xs text-ev-text-primary"
                  />
                  <Button variant="outline" onClick={copyLink}>
                    {copied ? (
                      <Check className="h-4 w-4 text-ev-success-text" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="flex justify-end">
                <Button variant="ghost" onClick={close}>
                  Done
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
